// Presentational card for one broadcast announcement, in three shapes:
//   variant="banner" — slim strip in the top stack
//   variant="toast"  — compact card in the corner
//   variant="modal"  — centered dialog body
// Pure: no fetching, no side effects. BroadcastCenter wires the actions; the
// superadmin compose page renders it with `preview` to show the real look.
import React from "react";
import { X, Megaphone } from "lucide-react";
import { SEVERITY_META, severityColors, localized, BROADCAST_STRINGS } from "./broadcastTheme";

const BroadcastCard = ({
  broadcast,
  language = "en",
  theme = "light",
  variant = "banner",
  onDismiss,
  onAck,
  onClose,
  preview = false,
}) => {
  const c = severityColors(broadcast.severity, theme);
  const Icon = (SEVERITY_META[broadcast.severity] || SEVERITY_META.INFO).icon || Megaphone;
  const { title, body, dir } = localized(broadcast, language);
  const t = BROADCAST_STRINGS[language === "ar" ? "ar" : "en"];
  const isModal = variant === "modal";
  const isToast = variant === "toast";

  const canDismiss = broadcast.dismissible && !broadcast.requireAck;
  const needsAck = broadcast.requireAck;

  const buttonBase = {
    border: "none",
    borderRadius: 8,
    padding: isModal ? "10px 18px" : "6px 12px",
    fontSize: isModal ? 14 : 12.5,
    fontWeight: 600,
    cursor: preview ? "default" : "pointer",
    whiteSpace: "nowrap",
  };

  return (
    <div
      dir={dir}
      role={isModal ? "document" : "status"}
      aria-live={broadcast.severity === "CRITICAL" ? "assertive" : "polite"}
      style={{
        display: "flex",
        gap: isModal ? 16 : 12,
        alignItems: isModal ? "flex-start" : "center",
        // Dark severity tints are translucent rgba — lay them over a solid
        // surface so the page / topbar never shows through a banner or toast.
        background: isModal
          ? (theme === "dark" ? "#1e293b" : "#ffffff")
          : `linear-gradient(${c.bg}, ${c.bg}), ${theme === "dark" ? "#0f172a" : "#ffffff"}`,
        color: theme === "dark" ? "#e2e8f0" : "#0f172a",
        border: `1px solid ${isModal ? (theme === "dark" ? "rgba(255,255,255,0.08)" : "#e2e8f0") : c.border}`,
        borderInlineStart: `4px solid ${c.accent}`,
        borderRadius: isModal ? 18 : 12,
        padding: isModal ? "28px 28px 24px" : isToast ? "14px 14px" : "10px 14px",
        boxShadow: isModal ? "0 24px 64px rgba(0,0,0,0.25)" : "0 6px 20px rgba(15,23,42,0.12)",
        width: "100%",
        boxSizing: "border-box",
        flexDirection: isModal ? "column" : "row",
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: isModal ? "center" : "flex-start", flex: 1, minWidth: 0 }}>
        <span
          aria-hidden="true"
          style={{
            flexShrink: 0,
            width: isModal ? 44 : 30,
            height: isModal ? 44 : 30,
            borderRadius: isModal ? 12 : 8,
            background: isModal ? c.bg : "transparent",
            color: c.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={isModal ? 24 : 18} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: isModal ? 18 : 14,
              color: isModal ? (theme === "dark" ? "#fff" : "#0f172a") : c.fg,
              lineHeight: 1.35,
              overflowWrap: "anywhere",
            }}
          >
            {title || t.announcement}
          </div>
          {!isModal && body && (
            <div style={{ fontSize: 13, marginTop: 2, lineHeight: 1.5, overflowWrap: "anywhere", whiteSpace: "pre-wrap", opacity: 0.9 }}>
              {body}
            </div>
          )}
        </div>
      </div>

      {isModal && body && (
        <div
          style={{
            fontSize: 14.5,
            lineHeight: 1.7,
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            color: theme === "dark" ? "rgba(226,232,240,0.9)" : "#334155",
            maxHeight: "50vh",
            overflowY: "auto",
          }}
        >
          {body}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0, alignSelf: isModal ? "flex-end" : "center" }}>
        {needsAck && (
          <button
            type="button"
            onClick={preview ? undefined : onAck}
            style={{ ...buttonBase, background: c.accent, color: "#fff" }}
          >
            {t.acknowledge}
          </button>
        )}
        {isModal && !needsAck && (
          <button
            type="button"
            onClick={preview ? undefined : canDismiss ? onDismiss : onClose}
            style={{ ...buttonBase, background: theme === "dark" ? "rgba(255,255,255,0.08)" : "#f1f5f9", color: theme === "dark" ? "#e2e8f0" : "#334155" }}
          >
            {t.close}
          </button>
        )}
        {!isModal && canDismiss && (
          <button
            type="button"
            aria-label={t.dismiss}
            title={t.dismiss}
            onClick={preview ? undefined : onDismiss}
            style={{ background: "transparent", border: "none", color: c.fg, cursor: preview ? "default" : "pointer", padding: 4, display: "flex", borderRadius: 6 }}
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

export default BroadcastCard;
